import type { FeeSchedule } from "@netkeep/fee-types";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { TRANSACTION_LABEL, VERIFICATION_LABEL, VERIFICATION_TONE } from "@/lib/format";
import { CUSTOM_PROFILE_ID } from "@/state/calculator";
import { Badge, Button, Card } from "./ui";

function describe(s: FeeSchedule): string {
  return `${s.paymentMethod.name} · ${TRANSACTION_LABEL[s.transactionType] ?? s.transactionType} · ${s.region}`;
}

/** Selects the platform + payment method + transaction type combination. */
export function ProfilePicker({
  schedules,
  selectedId,
  onSelect,
  children,
}: {
  schedules: FeeSchedule[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Extra content shown under the selection, inside the same card (e.g. the fee-data status line). */
  children?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const selected = schedules.find((s) => s.id === selectedId) ?? null;

  // A native modal <dialog> gives focus trapping, Esc to close and an inert background.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const choose = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <>
      <Card>
        <button
          type="button"
          className="picker__trigger"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-label={`Selling platform: ${selected ? selected.platform.name : "Custom fees"}. Change.`}
        >
          <span className="grow stack-xs">
            <span className="picker__kicker">SELLING ON</span>
            <span className="picker__title">{selected ? selected.platform.name : "Custom fees"}</span>
            <span className="picker__sub ellipsis">{selected ? describe(selected) : "Enter your own fees"}</span>
          </span>
          <span className="picker__change">Change</span>
        </button>
        {children}
      </Card>

      <dialog
        ref={dialogRef}
        className="sheet"
        aria-labelledby="picker-title"
        onClose={() => setOpen(false)}
        onClick={(e) => {
          // Clicking the backdrop (the dialog element itself) closes it.
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <h2 id="picker-title" className="page-title">
          Choose how you are selling
        </h2>
        <p className="muted">
          Each option is a complete fee set for that platform and payment method, so fees are never charged twice.
        </p>
        <ul className="sheet__list" role="radiogroup" aria-labelledby="picker-title">
          {[...schedules, null].map((item) => {
            const id = item?.id ?? CUSTOM_PROFILE_ID;
            return (
              <li key={id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={id === selectedId}
                  className="option"
                  onClick={() => choose(id)}
                >
                  <span className="grow stack-xs">
                    <span className="option__title">{item ? item.platform.name : "Custom fees"}</span>
                    <span className="option__sub">{item ? describe(item) : "Any platform — type in the fees yourself"}</span>
                  </span>
                  {item ? (
                    <Badge label={VERIFICATION_LABEL[item.verificationStatus]} tone={VERIFICATION_TONE[item.verificationStatus]} />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
        <Button title="Close" variant="secondary" onClick={() => setOpen(false)} />
      </dialog>
    </>
  );
}
